import { useEffect, useState } from 'react';
import { AutoComplete, InputGroup } from 'rsuite';
import { useAppSelector } from '@monorepo/shared/store/hook';
import CloseIcon from '@rsuite/icons/Close';
import { getClientTimezone } from '../utils/helper';
import { selectRegions } from '../store/authSlice';
import './TimezoneSelector.scss';

interface AutoCompleteItem {
  label: string;
  value: string;
  rawValue?: string;
  flag?: boolean;
  gmtValue?: string;
}

interface IProps {
  region: string;
  resetNum?: number;
  onSelectHandle?: (item?: AutoCompleteItem | Record<string, never>) => void;
}

export const TimezoneSelector = ({
  region,
  resetNum,
  onSelectHandle
}: IProps) => {
  const regions = useAppSelector(selectRegions);

  const clientTimezoneObj = getClientTimezone();

  const [timezones, setTimezones] = useState<AutoCompleteItem[]>([]);

  const [value, setValue] = useState<string>('');

  const [disabled, setDisabled] = useState<boolean>(false);

  const [fullTimezones, setFullTimezones] = useState<AutoCompleteItem[]>([]);

  const [defaultTimezones, setDefaultTimezones] = useState<AutoCompleteItem[]>(
    []
  );
  const [isShowFull, setIsShowFull] = useState<boolean>(false);

  const getDefaultOpts = (fullOpts: Array<Record<string, any>>) => {
    return fullOpts.filter(
      (opt) => opt?.gmtValue === clientTimezoneObj?.value || opt?.flag
    );
  };

  const getGmtValue = (obj: Record<string, any>) => {
    const r = new RegExp('([+-][0-9]{1,2}):');

    const GMT = obj?.description?.match(r)?.[1] || '+0';

    return GMT;
  };

  const setInitSelected = (
    fullOpts: Array<Record<string, any>>,
    defaultOpts: Array<AutoCompleteItem>,
    sysTimezone: string
  ) => {
    const sysTimezoneObj = fullOpts?.find(
      (obj) => obj?.rawValue === sysTimezone
    ) as AutoCompleteItem | undefined;

    if (defaultOpts?.[0]?.gmtValue === `${clientTimezoneObj.value}`) {
      setValue(defaultOpts[0].value + '');

      onSelectHandle?.(defaultOpts[0]);
    } else if (sysTimezoneObj?.value) {
      setValue(sysTimezoneObj.value + '');

      onSelectHandle?.(sysTimezoneObj);
    }
  };

  useEffect(() => {
    if (regions?.[region]?.timezone) {
      setDisabled(false);

      const sysTimezone = regions[region]?.defaultTimezone || '';

      const timezoneData = regions[region]?.timezone || [];

      const fullOpts: AutoCompleteItem[] = timezoneData.map(
        (obj: Record<string, any>) => ({
          label: obj?.description,
          value: obj?.description,
          rawValue: obj?.code,
          flag: obj?.is_major_tz,
          gmtValue: getGmtValue(obj)
        })
      );

      const defaultOpts = getDefaultOpts(fullOpts) as AutoCompleteItem[];

      const fullList: AutoCompleteItem[] = [
        // {
        //   label: '---- only show default options ----',
        //   value: 'less',
        //   flag: true
        // },
        ...fullOpts
      ];

      const defaultList: AutoCompleteItem[] = [
        ...defaultOpts
        // {
        //   label: '---- show full options ----',
        //   value: 'more',
        //   flag: true
        // }
      ];

      setFullTimezones(fullList);

      setDefaultTimezones(defaultList);

      setTimezones(defaultList);

      setInitSelected(fullOpts, defaultOpts, sysTimezone);
    } else {
      setFullTimezones([]);
      setDefaultTimezones([]);
      setTimezones([]);
      setValue('');
      setDisabled(true);

      onSelectHandle?.({});
    }
  }, [regions, region]);

  const resetHandle = () => {
    if (regions?.[region]?.timezone) {
      const sysTimezone = regions[region]?.defaultTimezone || '';

      setInitSelected(fullTimezones, defaultTimezones, sysTimezone);
    }
  };

  useEffect(() => {
    if (resetNum) {
      resetHandle();
    }
  }, [resetNum]);

  const styles: React.CSSProperties = {
    width: '100%',
    minWidth: '300px',
    display: 'inline-flex'
  };

  const filterBy = (keyword: string, item: any) => {
    // if (!keyword.trim() || timezones.some((obj) => obj?.label === keyword)) {
    //   return true;
    // }

    if (!keyword.trim()) return true;

    const lowerKeyword = keyword.toLocaleLowerCase();
    return `${item.label}`.toLocaleLowerCase().indexOf(lowerKeyword) >= 0;
  };

  // ---- reset
  const clear = () => {
    setValue('');
    onSelectHandle?.(undefined);
    setTimezones(fullTimezones);
  };

  return (
    <InputGroup
      inside
      style={styles}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <AutoComplete
        placement="bottomStart"
        disabled={disabled}
        value={value}
        data={timezones}
        filterBy={filterBy}
        renderListbox={(listbox) => {
          return (
            <div className="timezone-selector">
              <div
                onMouseDown={(e) => {
                  e.preventDefault(); // stops blur → stops close chain
                  e.stopPropagation();
                  if (isShowFull) {
                    setIsShowFull(false);
                    setTimezones(defaultTimezones);
                  } else {
                    setIsShowFull(true);
                    setTimezones(fullTimezones);
                  }
                  setValue('');
                }}
                style={{
                  padding: 10,
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee'
                }}
              >
                {isShowFull
                  ? '----- Show less options -----'
                  : '----- Show more options -----'}
              </div>
              {listbox}
            </div>
          );
        }}
        onChange={setValue}
        // onFocus={() => {
        //   if (value && !defaultTimezones.some((obj) => obj.value === value)) {
        //     setTimezones(fullTimezones);
        //   }
        // }}
        // onChange={(nextValue: string) => {
        //   if (nextValue !== 'more' && nextValue !== 'less') {
        //     setValue(nextValue);
        //   }
        // }}
        onSelect={(selectedValue: string) => {
          const selectedItem = timezones.find(
            (obj) => obj?.value === selectedValue
          );

          onSelectHandle?.(selectedItem);
        }}
      />

      {value && (
        <InputGroup.Button tabIndex={-1} onClick={clear}>
          <CloseIcon />
        </InputGroup.Button>
      )}
    </InputGroup>
  );
};

export default TimezoneSelector;
